import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

import { clsx } from "clsx";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button, ButtonProps } from "../ui/button";
import ImageSvg from "../ui/image-svg";

type ImageProps = {
  src: string;
  alt?: string;
};

type VideoProps = {
  image: ImageProps;
  url: string;
};

type TabProps = {
  heading: string;
  description: string;
  subDescription?: string;
  image?: ImageProps;
  video?: VideoProps;
};

type Props = {
  tagline: string;
  heading: string;
  description: string;
  buttons: ButtonProps[];
  tabs: TabProps[];
};

export type ModalidadeTransporteSectionProps =
  React.ComponentPropsWithoutRef<"section"> & Partial<Props>;

export const ModalidadeTransporteSection = (
  props: ModalidadeTransporteSectionProps,
) => {
  const { heading, tabs } = {
    ...ModalidadeTransporteSectionDefaults,
    ...props,
  };
  const [activeTab, setActiveTab] = useState(0);
  return (
    <section
      id="modalidade-transporte"
      className=" py-10 md:py-24 lg:py-28 bg-n-bg-green"
    >
      <div className="container">
        <div className="mx-auto mb-12 max-w-5xl text-center md:mb-18 lg:mb-20">
          <h2
            className="mb-5 text-4xl text-n-secondary md:mb-6 md:text-[56px]"
            style={{ fontFamily: "'Anton', sans-serif", lineHeight: "1.2em" }}
          >
            {heading}
          </h2>
        </div>

        <div className="grid grid-cols-1 items-center gap-x-12 md:grid-cols-2 lg:gap-x-20">
          <div className="relative mb-8 grid auto-cols-fr grid-cols-1 grid-rows-[auto_auto] items-start md:mb-0 md:items-stretch">
            {tabs.map((tab, index) => (
              <div
                key={index}
                onClick={() => setActiveTab(index)}
                className={clsx(
                  "cursor-pointer border-b border-border-primary py-6",
                  {
                    "opacity-100": activeTab === index,
                    "opacity-25": activeTab !== index,
                  },
                )}
              >
                <div className="flex items-center gap-2">
                  {/* <BsBoxFill className="text-2xl text-n-beige md:text-xl" /> */}

                  <h3 className="text-2xl font-semibold text-n-beige md:text-3xl md:leading-[1.3] lg:text-4xl">
                    {tab.heading}
                  </h3>
                </div>
                <motion.div
                  initial={false}
                  animate={{
                    height: activeTab === index ? "auto" : 0,
                    opacity: activeTab === index ? 1 : 0,
                  }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <p className="mt-3 text-n-beige font-semibold md:mt-4">
                    {tab.description}
                  </p>
                  {tab.subDescription && (
                    <p className="mt-1 text-n-beige ">{tab.subDescription}</p>
                  )}
                </motion.div>
              </div>
            ))}
          </div>
          <div className="max-h-full w-full justify-center overflow-hidden">
            <AnimatePresence mode="wait" initial={false}>
              {tabs.map((tab, index) => {
                if (activeTab !== index) return null;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    exit={{ opacity: 0 }}
                  >
                    {tab.image && (
                      <ImageSvg
                        src={tab.image.src}
                        alt={tab.image.alt}
                        className="h-full w-full"
                      />
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </div>

        <div className="flex items-center justify-center mt-20">
          <Link to="/fazer-cotacao">
            <Button id="btn-fazer-cotacao">
              Agendar meu transporte agora <ArrowRight className="ml-2 " />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export const ModalidadeTransporteSectionDefaults: Props = {
  tagline: "Modalidade de transporte",
  heading: "Escolha a sua modalidade de transporte",
  description:
    "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat.",
  buttons: [],
  tabs: [
    {
      heading: "Cegonha Convencional",
      description: "Solução econômica, flexível e inteligente. ",
      subDescription:
        "Ideal para quem busca o melhor custo-benefício. Esse modelo utiliza carga fracionada, com prazo de entrega flexível, aproveitando o fluxo logístico em todo o Brasil. ",
      image: {
        src: "cegonha-transporte-convencional-dark.svg",
        alt: "Cegonha Convencional",
      },
    },
    {
      heading: "Cegonha Expresso",
      description: "Agilidade com prioridade.",
      subDescription:
        "Prazo de entrega garantido, com prioridade tanto no embarque quanto no atendimento. Altamente recomendada para veículos especiais em longas distâncias.",
      image: {
        src: "cegonha-transporte-expresso-dark.svg",
        alt: "Cegonha Expresso",
      },
    },
    {
      heading: "Guincho aberto ou fechado",
      description: "Exclusividade ponta a ponta.",
      subDescription:
        "Transporte individual e direto, do ponto de origem ao destino final.A melhor opção para quem precisa de agilidade máxima ou tem um veículo que exige cuidado especial, como carros super esportivos ou de coleção.",
      image: {
        src: "transporte-fechado.svg",
        alt: "Guincho fechado",
      },
    },
  ],
};
